<script setup lang="ts">
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  useTemplateRef,
  watch,
} from 'vue';

import KBadge from '@/components/badges/KBadge.vue';
import KBtn from '@/components/btns/KBtn.vue';
import KMultipleAutocompleteField from '@/components/fields/KMultipleAutocompleteField.vue';
import SecondarySideBarMenuVListItem from '@/components/items/SecondarySideBarMenuVListItem.vue';
import DispatchPopoverVMenu from '@/components/vMenus/DispatchPopoverVMenu.vue';
import { useStaticStore } from '@/stores/useStaticStore';

// export interface Props {}
// const props = withDefaults(defineProps<Props>(), {});

const model = defineModel<{
  type?: string[] | null;
  zone?: string[] | null;
}>({
  default: () => ({}),
});

const btnEl = useTemplateRef('btn');
const panelsEl = useTemplateRef('panels');

const staticStore = useStaticStore();

const typeItems = computed(() => {
  return staticStore.getDriverTypes
    .filter((driverType) => {
      return !!driverType.type;
    })
    .map((driverType) => {
      return {
        // TODO as string
        value: driverType.type as string,
        // TODO as string
        title: driverType.type as string,
      };
    });
});

const zoneItems = computed(() => {
  return staticStore.getZones.map((zone) => {
    return {
      value: zone.code,
      title: zone.code,
    };
  });
});

// menu doesn't close with this
/* eslint-disable-next-line @typescript-eslint/no-empty-function */
function handleUpdateMenu() {}

function handleKeydownEnter() {
  isPopoverMenu.value = false;
}

function handleBtnClick() {
  isPopoverMenu.value = !isPopoverMenu.value;
}

const isTypeFieldMenu = ref(false);
const isZoneFieldMenu = ref(false);

const isPopoverMenu = ref(false);

watch(isPopoverMenu, function () {
  if (!isPopoverMenu.value) {
    isTypeFieldMenu.value = false;
    isZoneFieldMenu.value = false;
  }
  // HACK after closing not to break focus on other elements
  setTimeout(() => {
    if (isPopoverMenu.value) {
      if (activated.value[0] === 'type') {
        isTypeFieldMenu.value = true;
        isZoneFieldMenu.value = false;
      } else if (activated.value[0] === 'zone') {
        isTypeFieldMenu.value = false;
        isZoneFieldMenu.value = true;
      }
    }
  }, 0);
});

function outsideClickListener(event: MouseEvent): void {
  if (!event.target) return;
  const $panels = panelsEl.value;
  const $btn = btnEl.value?.$el;
  if (!$panels) return;
  if (!$btn) return;
  if (
    isPopoverMenu.value &&
    !$panels.contains(event.target as Node) &&
    !$btn.contains(event.target as Node)
  ) {
    isPopoverMenu.value = false;
  }
}

onMounted(() => {
  window.addEventListener('click', outsideClickListener, {
    // doesn't keep menu open when clicking on a datepicker
    capture: true,
  });
});

onUnmounted(() => {
  window.removeEventListener('click', outsideClickListener);
});

function handleClickActivate(value: {
  id: unknown;
  value: boolean;
  path: unknown[];
  event?: PointerEvent;
}) {
  if (value.value) return;
  // item cannot be deactivated
  void nextTick(() => {
    activated.value.push(value.id as string);
  });
}

function handleUpdateActivated() {
  if (activated.value[0] === 'type') {
    isTypeFieldMenu.value = true;
    isZoneFieldMenu.value = false;
  } else if (activated.value[0] === 'zone') {
    isTypeFieldMenu.value = false;
    isZoneFieldMenu.value = true;
  }
  setTimeout(() => {
    const $panels = panelsEl.value;
    if (!$panels) return;
    const $autofocus = $panels.querySelector('[autofocus]');
    if (!$autofocus) return;
    // @ts-expect-error
    $autofocus.focus();
  }, 0);
}

const activated = ref<string[]>(['type']);

const changesCount = computed(() => {
  return (model.value.type?.length || 0) + (model.value.zone?.length || 0);
});
</script>

<template>
  <fieldset
    :class="$style.dispatchDriverFiltersFieldset"
    data-testid="dispatchDriverFiltersFieldset"
  >
    <KBtn
      ref="btn"
      :class="$style.btn"
      variant="outlined"
      :active="isPopoverMenu"
      @click="handleBtnClick"
    >
      Driver Filters
      <template #append>
        <v-icon
          v-if="!changesCount"
          :class="$style.icon"
          icon="mdi-filter-variant"
        >
        </v-icon>
        <KBadge v-else class="mx-n1" :content="changesCount" inline />
      </template>
    </KBtn>
    <DispatchPopoverVMenu
      v-model="isPopoverMenu"
      disable-initial-focus
      :close-on-content-click="false"
      location="bottom"
      no-click-animation
      persistent
      scroll-strategy="none"
      :target="btnEl"
      transition="slide-y-transition"
    >
      <div ref="panels" :class="$style.panels">
        <div :class="$style.leftPanel">
          <v-list
            v-model:activated="activated"
            :class="$style.list"
            activatable
            @click:activate="handleClickActivate"
            @update:activated="handleUpdateActivated"
          >
            <SecondarySideBarMenuVListItem
              value="type"
              title="Type"
              class="!m-0"
            >
              <template #append>
                <KBadge
                  v-if="model.type?.length"
                  :content="model.type.length"
                  inline
                />
              </template>
            </SecondarySideBarMenuVListItem>
            <!-- zone field is not used
            <SecondarySideBarMenuVListItem
              value="zone"
              title="Zone"
              class="!m-0"
            >
              <template #append>
                <KBadge
                  v-if="model.zone?.length"
                  :content="model.zone.length"
                  inline
                />
              </template>
            </SecondarySideBarMenuVListItem>
            -->
          </v-list>
        </div>
        <div :class="$style.rightPanel">
          <v-window
            v-model="activated[0]"
            :touch="false"
            :class="$style.window"
          >
            <v-window-item value="type" :class="$style.windowItem">
              <KMultipleAutocompleteField
                v-model="model.type"
                :clearable="false"
                :menu-props="{
                  scrollStrategy: 'none',
                  attach: panelsEl,
                  contentClass: $style.menuContent,
                  eager: true,
                }"
                :autofocus="activated[0] === 'type'"
                :menu="isTypeFieldMenu"
                label="Type"
                :class="[$style.field, $style.multiSelectField]"
                :items="typeItems"
                @update:menu="handleUpdateMenu"
                @keydown.enter="handleKeydownEnter"
              />
            </v-window-item>
            <v-window-item value="zone" :class="$style.windowItem">
              <KMultipleAutocompleteField
                v-model="model.zone"
                :clearable="false"
                :menu-props="{
                  scrollStrategy: 'none',
                  attach: panelsEl,
                  contentClass: $style.menuContent,
                  eager: true,
                }"
                :autofocus="activated[0] === 'zone'"
                :menu="isZoneFieldMenu"
                label="Zone"
                :class="[$style.field, $style.multiSelectField]"
                :items="zoneItems"
                @update:menu="handleUpdateMenu"
                @keydown.enter="handleKeydownEnter"
              />
            </v-window-item>
          </v-window>
        </div>
      </div>
    </DispatchPopoverVMenu>
  </fieldset>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.dispatchDriverFiltersFieldset {
  border: 0;
  padding: 0;
  margin: 0;
}
  .btn {
    text-transform: none;
  }
    .append {
      position: relative;
      top: -1px;
      display: inline-block;
      margin-left: 6px;
      height: 20px;
    }
      .icon {
        font-size: 24px;
        margin: 0 -1px 0 -2px;
      }
  .panels {
    display: flex;
    height: calc(374px + 24px);
  }
    .leftPanel {
      width: 270px;
      overflow: auto;
      padding: 24px;
      border-right: 1px solid colors.$color-gray-100;
    }
      .list {
        padding: 0;
      }
    .rightPanel {
      width: 348px;
    }
      .window {
      }
        .windowItem {
          padding: 24px;
        }
  .menuContent {
    min-width: 300px !important;
    max-height: 310px !important;
    left: 294px !important;
  }
</style>
